import React from 'react';
import StepHeader from './StepHeader';
import { Field, Input, PhoneInput } from '../../lib/ui/Field';
import { ErrorBanner } from '../../lib/ui/Feedback';
import Button from '../../lib/ui/Button';

function formatAddr(addr) {
  if (!addr) return '';
  return [addr.house_no, addr.line1, addr.landmark, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ')
    || addr.label
    || (addr.lat != null ? `${addr.lat}, ${addr.lng}` : '');
}

export default function AddressStep({
  nameVal, setNameVal, phoneVal, setPhoneVal,
  savedAddresses, selectedAddressId, setSelectedAddressId,
  location, setLocation, locState, setLocState,
  openMapPicker, error, te, L,
  onClose, onBack, onContinue,
}) {
  const selectAddr = (addr) => {
    setSelectedAddressId(addr.id || null);
    setLocation({
      lat: parseFloat(addr.lat) || 0,
      lng: parseFloat(addr.lng) || 0,
      label: [addr.label, formatAddr(addr)].filter(Boolean).join(' · '),
      address_id: addr.id || null,
    });
    setLocState('got');
    if (addr.name) setNameVal(addr.name);
    if (addr.phone_contact) {
      const digits = String(addr.phone_contact).replace(/\D/g, '').slice(-10);
      if (digits.length === 10) setPhoneVal(digits);
    }
  };

  const selected = savedAddresses.find((a) => a.id === selectedAddressId);

  return (
    <div>
      <StepHeader title={`📍 ${L('Delivery Address', 'డెలివరీ చిరునామా')}`} onClose={onClose} />

      {savedAddresses.length > 0 ? (
        <Field label={L('Saved addresses', 'సేవ్ చేసిన చిరునామాలు')}>
          <div className="flex flex-col gap-1.5">
            {savedAddresses.map((addr) => {
              const active = selectedAddressId ? selectedAddressId === addr.id : location?.label === formatAddr(addr);
              return (
                <button
                  key={addr.id || formatAddr(addr)}
                  type="button"
                  onClick={() => selectAddr(addr)}
                  className={`text-left px-3.5 py-2.5 rounded-xl border-[1.5px] ${active ? 'bg-brand-light border-brand' : 'bg-brand-light/30 border-black/10'}`}
                >
                  <div className="text-[13px] font-extrabold text-brand-dark flex items-center gap-1.5">
                    {active ? '✅' : '📍'} {addr.label || 'Address'}
                    {addr.is_default ? <span className="text-[10px] bg-gold-light text-gold px-1.5 py-0.5 rounded-full">Default</span> : null}
                  </div>
                  {(addr.name || addr.phone_contact) ? (
                    <div className="text-xs text-ink-soft font-bold mt-0.5">{addr.name || ''}{addr.phone_contact ? ` · ${String(addr.phone_contact).replace(/^\+91/, '')}` : ''}</div>
                  ) : null}
                  <div className="text-xs text-ink-faint mt-0.5 leading-relaxed">{formatAddr(addr)}</div>
                </button>
              );
            })}
          </div>
        </Field>
      ) : null}

      {!selected ? (
        <>
          <Field label={L('Full name', 'పూర్తి పేరు')}>
            <Input placeholder={L('Your name', 'మీ పేరు')} value={nameVal} onChange={(e) => setNameVal(e.target.value)} />
          </Field>
          <Field label={L('Mobile number', 'మొబైల్ నంబర్')}>
            <PhoneInput value={phoneVal} onChange={(e) => setPhoneVal(e.target.value.replace(/[^0-9 ]/g, ''))} />
          </Field>
        </>
      ) : null}

      <Field label={L('Or use current location', 'లేదా ప్రస్తుత స్థానం')} tag={locState === 'got' && !selectedAddressId ? '✓ Set' : undefined}>
        {locState === 'got' && location && !selectedAddressId ? (
          <div className="bg-brand-light rounded-xl px-3 py-2.5 flex items-start gap-2">
            <span>📍</span>
            <span className="flex-1 font-bold text-[13px] text-brand-dark">{location.label}</span>
            <button type="button" onClick={() => { setLocation(null); setLocState('idle'); }} className="text-ink-faint">✕</button>
          </div>
        ) : (
          <Button variant="outline" fullWidth onClick={openMapPicker}>📍 {L('Use my location', 'నా స్థానం వాడండి')}</Button>
        )}
      </Field>

      <ErrorBanner>{error}</ErrorBanner>

      <div className="flex gap-2.5 mt-2">
        <Button variant="danger" onClick={onBack}>← {L('Back', 'వెనక్కి')}</Button>
        <Button variant="accent" className="flex-[2]" onClick={onContinue}>{L('Continue', 'కొనసాగించు')} →</Button>
      </div>
      <div className="mt-2.5 text-xs text-ink-faint leading-relaxed">
        {L('Name and phone are saved on each address (like Amazon). Edit them in Your account → Addresses.', 'పేరు మరియు ఫోన్ ప్రతి చిరునామాలో సేవ్ అవుతాయి. You → Addressesలో సవరించండి.')}
      </div>
    </div>
  );
}
